// Turning an analysis into the grouped report the panel shows.
//
// A flat list of hundreds of lines is unreadable, so findings are grouped by issue type
// with a count in each heading. Each section carries a `tone` the panel colours it with,
// so the different kinds of problem are told apart at a glance. Green is deliberately
// not used by any section - it reads as "nothing wrong" and every section here is a
// problem.

import { SECTION } from "./courseDocCheck";

export const SECTIONS = [
  { key: SECTION.STRUCTURE, heading: "COURSE NAME STRUCTURE", tone: "red" },
  { key: SECTION.SPACING, heading: "BLANK SPACES NEXT TO ~ (Fix Format trims them)", tone: "indigo" },
  { key: SECTION.CODE, heading: "SEGMENT 1 — MODULE CODE", tone: "blue" },
  { key: SECTION.NAME, heading: "SEGMENT 4 — MODULE NAME", tone: "amber" },
  { key: SECTION.COHORT, heading: "SEGMENT 5 — COHORT", tone: "violet" },
  {
    key: SECTION.COHORT_MISMATCH,
    heading: "COHORT MISMATCH (segment 5 vs category heading)",
    tone: "fuchsia",
  },
  { key: SECTION.TUTOR, heading: "SEGMENT 7 — TUTOR NAME MISMATCH (vs column E)", tone: "rose" },
  { key: SECTION.TUTOR_AMBIGUOUS, heading: "AMBIGUOUS TUTOR SURNAME (column E)", tone: "yellow" },
  { key: SECTION.SESSION, heading: "SEGMENT 8 — SESSION", tone: "cyan" },
  {
    key: SECTION.DUPLICATE,
    heading: "DUPLICATE GROUPS (same course, same group, same category and department)",
    tone: "orange",
  },
  // The catch-all. It must exist so no line is ever silently dropped.
  { key: SECTION.OTHER, heading: "OTHER", tone: "slate" },
];

const KNOWN_SECTIONS = SECTIONS.map((section) => section.key);

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Sort numerically by row and drop duplicate lines. */
const tidy = (lines) => {
  const seen = new Set();
  return lines
    .slice()
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .filter((line) => {
      if (seen.has(line.text)) return false;
      seen.add(line.text);
      return true;
    });
};

/**
 * Build the grouped Check Doc report.
 *
 * @param {object} analysis - from checkDocument
 * @param {{fileName: string}} meta
 * @returns {{summary, sections, footer, text, issues, rows}}
 */
export const buildCheckReport = (analysis, meta = {}) => {
  const lines = analysis.findings
    .map((finding) => ({
      rowNumber: finding.rowNumber,
      section: KNOWN_SECTIONS.indexOf(finding.section) === -1 ? SECTION.OTHER : finding.section,
      text: `Row ${finding.rowNumber}: ${finding.text}`,
    }))
    .concat(
      analysis.duplicateGroups.map((group) => ({
        rowNumber: group.repeated[0],
        section: SECTION.DUPLICATE,
        text: group.text,
      })),
      analysis.tutorGroups.map((group) => ({
        rowNumber: group.rowNumbers[0],
        section: SECTION.TUTOR_AMBIGUOUS,
        text: group.text,
      }))
    );

  const sections = SECTIONS.map((section) => {
    const own = tidy(lines.filter((line) => line.section === section.key));
    return { ...section, lines: own.map((line) => line.text), count: own.length };
  }).filter((section) => section.count > 0);

  const summary =
    `Check Doc found ${plural(analysis.counts.issues, "issue")} across ` +
    `${plural(analysis.counts.rows, "row")} ` +
    `(${plural(analysis.scanned, "row")} scanned, ${plural(analysis.courseRowCount, "course row")}).`;

  const footerLines = [];
  if (analysis.missingReference.length > 0) {
    footerLines.push(
      `Rows skipped (module name not found in course categories): ${analysis.missingReference.join(", ")}`
    );
  }
  const notCompared = Object.keys(analysis.cohortNotCompared);
  if (notCompared.length > 0) {
    footerLines.push(
      "Cohort not compared (the category heading names no month and year): " +
        notCompared
          .map((label) => `"${label}" rows ${analysis.cohortNotCompared[label].join(", ")}`)
          .join("; ")
    );
  }
  const footer = footerLines.join("\n");

  return {
    summary,
    sections,
    footer,
    issues: analysis.counts.issues,
    rows: analysis.counts.rows,
    text: toText(summary, sections, footer),
    fileName: meta.fileName || "",
  };
};

/**
 * Build the Fix Format report. Every single change appears in it - the user has no undo.
 */
export const buildFixReport = (plan, meta = {}) => {
  const sections = [];

  if (plan.changed.length > 0) {
    sections.push({
      key: "written",
      heading: meta.saved ? "FIXES WRITTEN TO THE FILE" : "FIXES READY TO WRITE",
      tone: "blue",
      count: plan.changed.length,
      lines: plan.changed
        .slice()
        .sort((a, b) => a.rowNumber - b.rowNumber)
        .map(
          (row) =>
            `Row ${row.rowNumber}: ` +
            row.changes
              .map((change) => `${change.label} "${change.before}" → "${change.after}"`)
              .join("; ")
        ),
    });
  }

  if (plan.incomplete.length > 0) {
    sections.push({
      key: "incomplete",
      heading: "FIXED BUT STILL INCOMPLETE — ADD A GROUP LETTER",
      tone: "amber",
      count: plan.incomplete.length,
      lines: plan.incomplete
        .slice()
        .sort((a, b) => a.rowNumber - b.rowNumber)
        .map((item) => `Row ${item.rowNumber}: ${item.reason}.`),
    });
  }

  if (plan.unresolved.length > 0) {
    sections.push({
      key: "unresolved",
      heading: "LEFT UNCHANGED — NEEDS A HUMAN",
      tone: "red",
      count: plan.unresolved.length,
      lines: plan.unresolved
        .slice()
        .sort((a, b) => a.rowNumber - b.rowNumber)
        .map((item) => `Row ${item.rowNumber}: ${item.reason} — fix manually.`),
    });
  }

  if (plan.skipped.length > 0) {
    sections.push({
      key: "skipped",
      heading: "SKIPPED — STRUCTURAL PROBLEM FOR CHECK DOC",
      tone: "violet",
      count: plan.skipped.length,
      lines: plan.skipped
        .slice()
        .sort((a, b) => a.rowNumber - b.rowNumber)
        .map((item) => `Row ${item.rowNumber}: ${item.reason}.`),
    });
  }

  // editWorkbookCells reports refs whose row is absent from the sheet XML. Those edits
  // silently did nothing, so they must not be reported as fixed.
  if (meta.skippedRefs && meta.skippedRefs.length > 0) {
    sections.push({
      key: "unwritten",
      heading: "COULD NOT BE WRITTEN (row missing from the sheet)",
      tone: "red",
      count: meta.skippedRefs.length,
      lines: [meta.skippedRefs.join(", ")],
    });
  }

  const summary =
    plan.rowsChanged === 0
      ? `Fix Format found nothing to change (${plural(plan.scanned, "row")} scanned, ` +
        `${plural(plan.courseRowCount, "course row")}).`
      : `Fix Format ${meta.saved ? "updated" : "would update"} ${plural(plan.rowsChanged, "row")} ` +
        `(${plural(plan.scanned, "row")} scanned).`;

  return {
    summary,
    sections,
    footer: "",
    changed: plan.rowsChanged,
    text: toText(summary, sections, ""),
    fileName: meta.fileName || "",
  };
};

/** The plain-text form, for copying and for the status banner. */
const toText = (summary, sections, footer) => {
  const blocks = [summary];
  sections.forEach((section) => {
    blocks.push(`${section.heading} (${section.count})\n${section.lines.join("\n")}`);
  });
  if (footer) blocks.push(footer);
  return blocks.join("\n\n");
};
